import React, { useState } from 'react';
import { Phone, MessageCircle, MapPin, Clock, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    console.log('Form Data:', formData);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I would like to inquire about your AC services.");
    window.open(`https://wa.me/97474408801?text=${message}`, '_blank');
  };

  const services = [
    'AC Installation',
    'AC Repair',
    'Spare Parts',
    '24/7 Maintenance',
    'AMC Services',
    'General Inquiry'
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      details: '+974 7008 6090',
      action: 'tel:+97470086090',
      description: 'Available 24/7 for emergency services',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      details: '+974 7440 8801',
      action: handleWhatsAppClick,
      description: 'Quick response guaranteed',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Muaither, Doha - Qatar',
      action: '#',
      description: 'Office hours: 8 AM - 6 PM',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: 'info@nebulaqa.com',
      action: 'mailto:info@nebulaqa.com',
      description: 'Response within 24 hours',
      color: 'from-orange-500 to-orange-600'
    }
  ];

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-purple-50 rounded-full px-4 py-2 border border-purple-200 mb-6">
              <span className="text-purple-700 font-medium text-sm">Get In Touch</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Contact <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Us</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Get in touch with our team for professional AC services, free quotes, 
              or emergency support. We're here to help 24/7 across Qatar.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {contactInfo.map((contact, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className={`bg-gradient-to-r ${contact.color} w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <contact.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{contact.title}</h3>
                {typeof contact.action === 'string' && contact.action.startsWith('tel:') ? (
                  <a
                    href={contact.action}
                    className="text-purple-600 hover:text-purple-700 transition-colors font-semibold text-lg block mb-2"
                  >
                    {contact.details}
                  </a>
                ) : typeof contact.action === 'string' && contact.action.startsWith('mailto:') ? (
                  <a
                    href={contact.action}
                    className="text-purple-600 hover:text-purple-700 transition-colors font-semibold text-lg block mb-2"
                  >
                    {contact.details}
                  </a>
                ) : typeof contact.action === 'function' ? (
                  <button
                    onClick={contact.action}
                    className="text-purple-600 hover:text-purple-700 transition-colors font-semibold text-lg block mb-2 w-full"
                  >
                    {contact.details}
                  </button>
                ) : (
                  <span className="text-purple-600 font-semibold text-lg block mb-2">{contact.details}</span>
                )}
                <p className="text-gray-600 text-sm">{contact.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Send us a Message</h2>
              
              {isSubmitted && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 flex items-center">
                  <CheckCircle size={20} className="text-green-600 mr-3" />
                  <span className="text-green-800 font-medium">Message sent successfully! We'll get back to you soon.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-800 text-sm font-semibold mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-800 text-sm font-semibold mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                      placeholder="+974 XXXX XXXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-800 text-sm font-semibold mb-3">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-gray-800 text-sm font-semibold mb-3">
                    Service Required
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-800 text-sm font-semibold mb-3">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
                    placeholder="Describe your requirements or ask any questions..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-6 rounded-xl font-bold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-xl"
                >
                  <Send size={20} />
                  <span>Send Message</span>
                </button>
              </form>
            </div>

            {/* Location & Emergency Info */}
            <div className="space-y-8">
              {/* Location Info */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Location & Hours</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-purple-600 p-3 rounded-xl">
                      <MapPin size={24} className="text-white" />
                    </div>
                    <div>
                      <h4 className="text-gray-900 font-bold text-lg mb-1">Office Address</h4>
                      <p className="text-gray-600 leading-relaxed">
                        C.R. No: 84110, Add: Office No: 1,<br />
                        Bldg No. 7, Behind FSC,<br />
                        Muaither, Doha - Qatar
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 p-3 rounded-xl">
                      <Clock size={24} className="text-white" />
                    </div>
                    <div>
                      <h4 className="text-gray-900 font-bold text-lg mb-1">Business Hours</h4>
                      <div className="text-gray-600 space-y-1">
                        <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                        <p>Saturday: 8:00 AM - 4:00 PM</p>
                        <p className="text-green-600 font-bold">Emergency: 24/7 Available</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-8 text-white">
                <div className="flex items-center mb-6">
                  <AlertCircle size={28} className="mr-3" />
                  <h3 className="text-2xl font-bold">Emergency Service</h3>
                </div>
                <p className="text-red-100 mb-6 leading-relaxed">
                  AC breakdown? Need urgent repair? Our emergency team is available 24/7 
                  to handle your cooling emergencies anywhere in Qatar.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="tel:+97470086090"
                    className="bg-white text-red-600 px-6 py-3 rounded-xl font-bold hover:bg-red-50 transition-colors duration-300 text-center flex items-center justify-center space-x-2 shadow-lg"
                  >
                    <Phone size={20} />
                    <span>Emergency Call</span>
                  </a>
                  <button
                    onClick={handleWhatsAppClick}
                    className="bg-green-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-600 transition-colors duration-300 text-center flex items-center justify-center space-x-2 shadow-lg"
                  >
                    <MessageCircle size={20} />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>

              {/* Quick Contact */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Need a Quick Quote?</h3>
                <p className="mb-6 opacity-90">Call us now for immediate assistance and free estimates</p>
                <a
                  href="tel:+97470086090"
                  className="inline-flex items-center bg-white text-purple-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors duration-300 shadow-lg text-lg"
                >
                  <Phone size={24} className="mr-3" />
                  +974 7008 6090
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;